import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';

interface TaxIdFieldProps {
  value: string;
  onChange: (value: string) => void;
  onError?: (error: string) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
  sx?: TextFieldProps['sx'];
  color?: TextFieldProps['color'];
  variant?: TextFieldProps['variant'];
  readOnly?: boolean;
  id?: string;
}

/**
 * Componente reutilizável para entrada de CPF ou CNPJ
 * - Formata automaticamente o valor digitado
 * - Valida se tem 11 dígitos (CPF) ou 14 dígitos (CNPJ)
 * - Remove caracteres não numéricos antes de retornar o valor
 */
const TaxIdField: React.FC<TaxIdFieldProps> = ({
  value,
  onChange,
  onError,
  error = false,
  helperText,
  required = false,
  label = 'CPF ou CNPJ',
  placeholder = '000.000.000-00 ou 00.000.000/0000-00',
  fullWidth = true,
  sx,
  color = 'secondary',
  variant = 'outlined',
  readOnly = false,
  id = 'tax-id-field',
}) => {
  /**
   * Formata CPF (11 dígitos) ou CNPJ (14 dígitos)
   */
  const formatTaxId = (inputValue: string): string => {
    // Remove tudo que não é dígito
    const numbers = inputValue.replaceAll(/\D/g, '');
    
    // Formata CPF (11 dígitos) ou CNPJ (14 dígitos)
    if (numbers.length <= 11) {
      // CPF: 000.000.000-00
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      // CNPJ: 00.000.000/0000-00
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
  };

  /**
   * Valida se o CPF/CNPJ tem o número correto de dígitos
   */
  const validateTaxId = (inputValue: string): { isValid: boolean; error?: string } => {
    const cleanValue = inputValue.replaceAll(/\D/g, '');
    
    if (!cleanValue && required) {
      return { isValid: false, error: 'CPF ou CNPJ é obrigatório' };
    }
    
    if (cleanValue && cleanValue.length !== 11 && cleanValue.length !== 14) {
      return { isValid: false, error: 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos' };
    }
    
    return { isValid: true };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formatted = formatTaxId(inputValue);
    
    // Validar e informar erro se houver callback
    if (onError) {
      const validation = validateTaxId(formatted);
      if (!validation.isValid && validation.error) {
        onError(validation.error);
      } else {
        onError('');
      }
    }
    
    onChange(formatted);
  };

  return (
    <TextField
      id={id}
      fullWidth={fullWidth}
      label={label}
      variant={variant}
      color={color}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      error={error}
      helperText={helperText}
      slotProps={{ 
        htmlInput: { 
          maxLength: 18,
          readOnly: readOnly
        } 
      }}
      sx={sx}
    />
  );
};

/**
 * Função utilitária para validar CPF/CNPJ
 * Pode ser usada externamente
 */
export const validateTaxId = (value: string, required: boolean = true): { isValid: boolean; error?: string } => {
  const cleanValue = value.replaceAll(/\D/g, '');
  
  if (!cleanValue) {
    if (required) {
      return { isValid: false, error: 'CPF ou CNPJ é obrigatório' };
    }
    return { isValid: true };
  }
  
  if (cleanValue.length !== 11 && cleanValue.length !== 14) {
    return { isValid: false, error: 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos' };
  }
  
  return { isValid: true };
};

/**
 * Função utilitária para formatar CPF/CNPJ
 * Pode ser usada externamente
 */
export const formatTaxId = (value: string): string => {
  const numbers = value.replaceAll(/\D/g, '');
  
  if (numbers.length <= 11) {
    // CPF: 000.000.000-00
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    // CNPJ: 00.000.000/0000-00
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }
};

/**
 * Função utilitária para obter apenas os números do CPF/CNPJ
 * Pode ser usada externamente
 */
export const cleanTaxId = (value: string): string => {
  return value.replaceAll(/\D/g, '');
};

export default TaxIdField;
